import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { IconButton } from "components/atoms/IconButton";
import { useTheme } from "styled-components";
import { Column16 } from "@carbon/icons-react";
import { IconType } from "react-icons";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { TableInstance } from "react-table";
import {
  TableCellIds,
  UserColumnsConfig,
} from "components/atoms/Table/types/index";
import { updateUserColumns } from "services/api/users";
import { setUserColumns } from "store/authSlice";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ColumnDragItem } from "./ColumnDragItem";
import { RootState } from "store";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { getTableKeyFromStatus } from "utils/shipmentTableKeys";
import { normalizeColumnId } from "utils/normalizeColumnId";
import { mergeColumnOrder } from "components/atoms/Table/utils/mergeColumnOrder";

const orderEquals = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export const ColumnsDropdown = <T extends object>(
  props: TableInstance<T> & { tableKey?: string },
) => {
  const {
    allColumns,
    setHiddenColumns,
    state: { hiddenColumns, columnOrder },
    setColumnOrder,
    userColumns,
    tableKey: propTableKey,
  } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const router = useRouter();

  // Determine tableKey based on router query or prop
  const tableKey = useMemo(() => {
    if (propTableKey) return propTableKey;
    const status = router.query.status as string;
    return getTableKeyFromStatus(status);
  }, [propTableKey, router.query.status]);

  // Get full user columns from Redux state
  const fullUserColumns = useSelector(
    (state: RootState) => state.authSlice.userColumns,
  );

  // Local state for order to optimize drag performance
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState<boolean>(true);

  // Latest order, readable synchronously from the drag-end handler without a
  // stale closure.
  const localColumnOrderRef = useRef<string[]>([]);
  useEffect(() => {
    localColumnOrderRef.current = localColumnOrder;
  }, [localColumnOrder]);

  // Snapshot of the order when the current drag started, so on drop we can tell
  // whether this gesture actually changed anything (vs. the live Redux/DB order,
  // which may be stale mid-flight) and so a cancelled drag can be reverted.
  // null means no drag is in progress.
  const dragStartOrderRef = useRef<string[] | null>(null);

  // Get displayable columns (exclude checkbox, only string headers)
  const getDisplayableColumns = useCallback(() => {
    return allColumns.filter(
      (col) =>
        col.id !== TableCellIds.CHECKBOX && typeof col.Header === "string",
    );
  }, [allColumns]);

  // Initialize column order
  useEffect(() => {
    setIsLoadingColumns(true);

    try {
      // Get the configuration for this specific tab
      const tabConfig = fullUserColumns?.[tableKey] as UserColumnsConfig;
      const savedOrder = tabConfig?.columnOrder;

      // Get current displayable columns (those actually present in this tab)
      const currentColumns = getDisplayableColumns();
      const currentColumnIds = currentColumns.map((col) =>
        normalizeColumnId(col.id as string),
      );

      // Use the table's actual column order if available and it has non-checkbox columns
      if (columnOrder && columnOrder.length > 0) {
        // Filter the table's column order to exclude checkbox and only include displayable columns
        const tableColumnOrder = columnOrder.filter(
          (id) => id !== TableCellIds.CHECKBOX && currentColumnIds.includes(id),
        );

        // Only use table order if we got valid columns, otherwise fall through to other options
        if (tableColumnOrder.length > 0) {
          // Columns the order does not know yet go next to their definition
          // neighbour, matching where the Table renders them.
          setLocalColumnOrder(
            mergeColumnOrder(tableColumnOrder, currentColumnIds),
          );
          setIsLoadingColumns(false);
          return;
        }
      }

      // Use saved order if available
      if (savedOrder && savedOrder.length > 0) {
        // Filter saved order to only include columns that exist in current tab
        const validOrder = savedOrder.filter(
          (id) => currentColumnIds.includes(id) && id !== TableCellIds.CHECKBOX,
        );

        // Add any new columns that aren't in saved order, next to their
        // definition neighbour
        const finalOrder = mergeColumnOrder(validOrder, currentColumnIds);

        if (finalOrder.length > 0) {
          setLocalColumnOrder(finalOrder);
          setIsLoadingColumns(false);
          return;
        }
      }

      // Fallback: use current column IDs
      if (currentColumnIds.length > 0) {
        setLocalColumnOrder(currentColumnIds);
      }

      setIsLoadingColumns(false);
    } catch (error) {
      console.error("Error initializing column order:", error);
      setIsLoadingColumns(false);
    }
  }, [
    fullUserColumns,
    tableKey,
    getDisplayableColumns,
    allColumns,
    columnOrder,
  ]);

  // Hidden column IDs: the saved tab config laid over the page's userColumns.
  // userColumns also carries the page defaults (columns that start hidden)
  // and is what the Table hides columns by, so this keeps the ticks in step
  // with the table for columns the saved tab config never mentions.
  const currentUserHiddenColumns = useMemo(() => {
    const tabConfig = {
      ...(userColumns || {}),
      ...(fullUserColumns?.[tableKey] || {}),
    };

    return Object.keys(tabConfig).filter(
      (key) => key !== "columnOrder" && !tabConfig[key],
    );
  }, [fullUserColumns, tableKey, userColumns]);

  // Save columns to database
  const saveColumnsToDatabase = useCallback(
    async (updatedConfig: any) => {
      try {
        await updateUserColumns(updatedConfig);
        dispatch(setUserColumns(updatedConfig));
      } catch (error: any) {
        // Extract server message if available, otherwise use generic message
        const errorMessage =
          error?.response?.data?.message ||
          "Failed to save column settings. Please try again.";
        toast.error(errorMessage);
        console.error("Error saving column configuration:", error);
      }
    },
    [dispatch],
  );

  // Drag started — snapshot the order so we can later tell whether the gesture
  // changed anything and can revert if it is cancelled.
  const handleDragStart = useCallback(() => {
    dragStartOrderRef.current = localColumnOrderRef.current;
  }, []);

  // Live reorder while dragging: update local state only (cheap, no network,
  // no react-table writes). Persisting happens once on drop. Uses a functional
  // update so rapid consecutive hovers always splice the freshest order.
  const moveColumn = useCallback((dragIndex: number, hoverIndex: number) => {
    setLocalColumnOrder((prev) => {
      const newOrder = [...prev];
      const [draggedItem] = newOrder.splice(dragIndex, 1);
      newOrder.splice(hoverIndex, 0, draggedItem);

      // Ensure no checkbox column in the order
      const sanitizedOrder = newOrder.filter(
        (id) => id !== TableCellIds.CHECKBOX,
      );

      localColumnOrderRef.current = sanitizedOrder;
      return sanitizedOrder;
    });
  }, []);

  // Drag ended. `didDrop` is react-dnd's monitor.didDrop(): true when released
  // on a row, false when cancelled (ESC) or released outside the list.
  const handleDragEnd = useCallback(
    (didDrop: boolean) => {
      const startOrder = dragStartOrderRef.current;
      dragStartOrderRef.current = null;

      const finalOrder = localColumnOrderRef.current.filter(
        (id) => id !== TableCellIds.CHECKBOX,
      );

      // Cancelled drag — revert the live list to where it started; persist
      // nothing. (Nothing was written to react-table during hover, so reverting
      // local state is enough to keep everything consistent.)
      if (!didDrop) {
        if (startOrder && !orderEquals(finalOrder, startOrder)) {
          localColumnOrderRef.current = startOrder;
          setLocalColumnOrder(startOrder);
        }
        return;
      }

      // Real drop that didn't actually move anything (grabbed and released in
      // place) — skip the react-table write and the network round-trip. We
      // compare against this gesture's start, not the Redux/DB order, which can
      // be stale while a previous save is in flight.
      if (!startOrder || orderEquals(finalOrder, startOrder)) return;

      // Update react-table. Keep the checkbox column pinned first (matching how
      // the Table builds its order) so it doesn't flicker to the end.
      if (setColumnOrder) {
        const hasCheckbox = allColumns.some(
          (col) => col.id === TableCellIds.CHECKBOX,
        );
        setColumnOrder(
          hasCheckbox ? [TableCellIds.CHECKBOX, ...finalOrder] : finalOrder,
        );
      }

      // Save to database with table-specific key.
      const currentTabConfig = fullUserColumns?.[tableKey] || {};
      const updatedConfig = {
        ...fullUserColumns,
        [tableKey]: {
          ...currentTabConfig,
          columnOrder: finalOrder,
        },
      };
      saveColumnsToDatabase(updatedConfig);
    },
    [allColumns, setColumnOrder, fullUserColumns, saveColumnsToDatabase, tableKey],
  );

  // Handle visibility toggle
  const handleToggleVisibility = useCallback(
    (columnId: string, isVisible: boolean) => {
      // Initialize tab config if it doesn't exist
      const currentTabConfig = fullUserColumns?.[tableKey] || {};

      const updatedVisibility = {
        ...currentTabConfig,
        [columnId]: isVisible,
      };

      // Remove columnOrder from visibility object
      const { columnOrder: _, ...visibilityOnly } = updatedVisibility as any;

      // Ensure checkbox is not in the saved order
      const sanitizedOrder = localColumnOrder.filter(
        (id) => id !== TableCellIds.CHECKBOX,
      );

      const updatedConfig = {
        ...fullUserColumns,
        [tableKey]: {
          ...visibilityOnly,
          columnOrder: sanitizedOrder,
        },
      };

      saveColumnsToDatabase(updatedConfig);

      // Update local hidden columns for react-table
      if (!isVisible) {
        setHiddenColumns([...hiddenColumns, columnId]);
      } else {
        setHiddenColumns(hiddenColumns.filter((id) => id !== columnId));
      }
    },
    [
      fullUserColumns,
      localColumnOrder,
      hiddenColumns,
      setHiddenColumns,
      saveColumnsToDatabase,
      tableKey,
    ],
  );

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton as="div">
          <IconButton
            icon={Column16 as unknown as IconType}
            tooltip="Columns"
          />
        </MenuButton>
      </div>
      <MenuItems
        transition
        style={{
          width: 240,
          borderRadius: theme.borderRadius,
          backgroundColor: theme.dropdownBackgroundColor,
          borderColor: theme.dropdownBorderColor,
          boxShadow: theme.shadows["2xl"],
        }}
        className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in border"
      >
        <DndProvider backend={HTML5Backend}>
          <div className="py-1.5 max-h-[60vh] overflow-y-auto">
            <div
              className="px-4 py-2"
              style={{ borderBottom: `1px solid ${theme.borderColor}` }}
            >
              <div className="flex justify-between text-xs text-gray-500">
                <div>Drag to reorder</div>
                <div
                  className="w-px h-4 mx-2 bg-gray-300"
                  style={{ backgroundColor: theme.borderColor }}
                />
                <div>Toggle visibility</div>
              </div>
            </div>
            {isLoadingColumns ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                Loading columns...
              </div>
            ) : localColumnOrder.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No columns available
              </div>
            ) : (
              localColumnOrder.map((columnId: string, index) => {
                // Skip checkbox column entirely
                if (columnId === TableCellIds.CHECKBOX) {
                  return null;
                }

                const column = allColumns.find((col) => {
                  const colId = normalizeColumnId(col.id as string);
                  return colId === columnId;
                });

                // Double check - don't render checkbox columns
                if (!column || column.id === TableCellIds.CHECKBOX) {
                  return null;
                }

                const cID = normalizeColumnId(column.id as string);
                return (
                  <ColumnDragItem
                    key={cID}
                    column={column}
                    index={index}
                    moveColumn={moveColumn}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isHidden={currentUserHiddenColumns.includes(cID)}
                    onToggleVisibility={(checked) =>
                      handleToggleVisibility(cID, checked)
                    }
                  />
                );
              })
            )}
          </div>
        </DndProvider>
      </MenuItems>
    </Menu>
  );
};
